import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-effect shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('accueil')}>
            <img 
              src={businessInfo.logo} 
              alt="DNA Barbershop Logo" 
              className="h-12 w-auto transition-transform duration-300 hover:scale-110"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('accueil')}
              className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium"
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => scrollToSection('equipe')}
              className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium"
            >
              {t.nav.team}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium"
            >
              {t.nav.services}
            </button>
            <button
              onClick={() => scrollToSection('avis')}
              className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium"
            >
              {t.nav.reviews}
            </button>
            <button
              onClick={() => scrollToSection('galerie')}
              className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium"
            >
              {t.nav.gallery}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium"
            >
              {t.nav.contact}
            </button>
          </nav>

          {/* Language Toggle & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-4 py-2 rounded-full glass-effect border border-white/20 text-white hover:bg-white/10 transition-all duration-300 hover:scale-105"
            >
              <Globe className="w-4 h-4" />
              <span className="font-semibold">{language === 'fr' ? 'EN' : 'FR'}</span>
            </button>
            <a href="tel:4389221776">
              <Button
                className="bg-gradient-to-r from-[#00D9FF] to-[#FF1493] text-white hover:shadow-lg hover:shadow-[#00D9FF]/50 transition-all duration-300 font-semibold border-0 hover:scale-105"
              >
                <Phone className="w-4 h-4 mr-2" />
                (438) 922-1776
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white relative z-50"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden glass-effect border-t border-white/10 py-4 animate-fade-in">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('accueil')}
                className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium text-left px-4"
              >
                {t.nav.home}
              </button>
              <button
                onClick={() => scrollToSection('equipe')}
                className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium text-left px-4"
              >
                {t.nav.team}
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium text-left px-4"
              >
                {t.nav.services}
              </button>
              <button
                onClick={() => scrollToSection('avis')}
                className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium text-left px-4"
              >
                {t.nav.reviews}
              </button>
              <button
                onClick={() => scrollToSection('galerie')}
                className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium text-left px-4"
              >
                {t.nav.gallery}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-white/90 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#00D9FF] hover:to-[#FF1493] transition-all duration-200 font-medium text-left px-4"
              >
                {t.nav.contact}
              </button>
              <div className="px-4 pt-2 flex gap-2">
                <button
                  onClick={toggleLanguage}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-full glass-effect border border-white/20 text-white"
                >
                  <Globe className="w-4 h-4" />
                  <span className="font-semibold">{language === 'fr' ? 'EN' : 'FR'}</span>
                </button>
                <a href="tel:4389221776" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-[#00D9FF] to-[#FF1493] text-white hover:shadow-lg hover:shadow-[#00D9FF]/50 transition-all duration-300">
                    <Phone className="w-4 h-4 mr-2" />
                    {t.hero.call}
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
